import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Download } from 'lucide-react';
import { generateProtectedWithdrawalPDF } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { API_ENDPOINTS } from '../config/api';

import useLockBodyScroll from '../utils/useLockBodyScroll';

const Bank = () => {
  const { user } = useAuth();
  const { showAlert } = useAlert();

  // Withdrawal Request States
  const [walletBalance, setWalletBalance] = useState(0);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestWallet, setRequestWallet] = useState('main_wallet');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // History & Modal States
  const [bankDetails, setBankDetails] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState({ title: '', content: '', password: '' });
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useLockBodyScroll(showModal || showPasswordPrompt);

  const fetchBankData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(API_ENDPOINTS.WITHDRAWAL, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setBankDetails(data.bank_details);
        setWithdrawals(data.withdrawals);
        setTransactions(data.transactions || []);
        setWalletBalance(parseFloat(data.wallet_balance || 0));
      }
    } catch (error) {
      console.error("Failed to fetch bank data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankData();
  }, []);

  // Confirmation Modal States
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [editableBankDetails, setEditableBankDetails] = useState({
    bank_name: '', account_number: '', ifsc_code: '', branch_name: '', account_holder: ''
  });
  const [transactionPassword, setTransactionPassword] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleIFSCBlur = async () => {
    const code = editableBankDetails.ifsc_code;
    if (!code || code.length !== 11) return;

    try {
      const response = await fetch(`https://ifsc.razorpay.com/${code}`);
      if (response.ok) {
        const data = await response.json();
        setEditableBankDetails(prev => ({
          ...prev,
          bank_name: data.BANK,
          branch_name: data.BRANCH
        }));
      }
    } catch (e) {
      console.error("IFSC Lookup Failed", e);
    }
  };

  const initiateWithdrawal = (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!requestAmount || parseFloat(requestAmount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    if (parseFloat(requestAmount) > walletBalance) {
      setError('Insufficient balance in selected wallet.');
      return;
    }

    // Populate editable details from fetched bankDetails
    setEditableBankDetails({
      bank_name: bankDetails?.bank_name || '',
      account_number: bankDetails?.account_number || '',
      ifsc_code: bankDetails?.ifsc_code || '',
      branch_name: bankDetails?.branch_name || '', // Assuming branch exists in DB
      account_holder: bankDetails?.account_holder || '' // Assuming holder exists
    });

    setShowConfirmModal(true);
    setIsEditingBank(false);
    setTransactionPassword('');
  };



  const submitWithdrawal = async () => {
    if (!transactionPassword) {
      showAlert("Please enter Transaction Password", 'error');
      return;
    }

    setConfirmLoading(true);
    try {
      const token = localStorage.getItem('authToken');

      const payload = {
        amount: requestAmount,
        wallet_type: requestWallet,
        transaction_password: transactionPassword,
        update_bank_details: isEditingBank,
        bank_details: isEditingBank ? editableBankDetails : null
      };

      const response = await fetch(`${API_ENDPOINTS.WITHDRAWAL.replace('withdrawals.php', 'request_withdrawal.php')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Withdrawal request submitted successfully!');
        setRequestAmount('');
        setShowConfirmModal(false);
        fetchBankData();
      } else {
        showAlert(data.message || 'Failed to submit request.', 'error');
      }
    } catch (err) {
      showAlert('An error occurred. Please try again.', 'error');
    } finally {
      setConfirmLoading(false);
    }
  };



  const downloadWithdrawalHistory = () => {
    setShowPasswordPrompt(true);
  };
  const handlePasswordSubmit = async () => {
    if (!passwordInput) {
      setPasswordError('Please enter your transaction password.');
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_ENDPOINTS.WITHDRAWAL.replace('withdrawals.php', 'verify_transaction_password.php')}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ password: passwordInput })
      });
      const data = await response.json();

      if (data.success) {
        // Password correct, generate PDF
        setShowPasswordPrompt(false);
        setPasswordInput('');
        setPasswordError('');

        const validUserId = user?.user_id || 'DEMO';
        const userName = user?.name || 'User';

        // Generate PDF (Protect with User ID still, or Transaction Password? standard is User ID)
        await generateProtectedWithdrawalPDF(withdrawals, bankDetails, validUserId, userName);

        // Show success modal
        setModalMessage({
          title: 'Receipt Downloaded Successfully!',
          content: 'PDF file has been downloaded to your device',
          password: ''
        });
        setShowModal(true);
      } else {
        setPasswordError(data.message || 'Incorrect transaction password.');
      }
    } catch (error) {
      setPasswordError('Verification failed. Please try again.');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Payouts & Withdrawals</h1>
          <p className="text-gray-600 mt-1">Manage your withdrawals and view transaction history</p>
        </div>

        {/* Withdrawal Request Form */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
            <h3 className="text-lg font-semibold opacity-90 mb-1">Available Balance</h3>
            <div className="text-4xl font-bold mb-6">₹ {walletBalance.toLocaleString('en-IN')}</div>
            <div className="text-sm opacity-75">
              <p>• Only Main Wallet balance is withdrawable.</p>
              <p>• Shopping Wallet points cannot be withdrawn.</p>
            </div>
          </div>

          <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-800 mb-4">Request New Withdrawal</h3>
            {error && <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
            {success && <div className="mb-4 p-3 bg-green-50 text-green-600 rounded-lg text-sm">{success}</div>}

            <form onSubmit={initiateWithdrawal} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Wallet</label>
                <select
                  value={requestWallet}
                  onChange={(e) => setRequestWallet(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50"
                >
                  <option value="main_wallet">Main Wallet (Income)</option>
                  {/* Shopping Wallet option is explicitly excluded as per requirements */}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  min="1"
                  step="0.01"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Enter amount"
                />
              </div>
              <div className="md:col-span-2">
                <button
                  type="submit"
                  className="w-full py-3 rounded-lg font-semibold text-white transition-all bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg"
                >
                  Submit Withdrawal Request
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-800">Withdrawal History</h3>
            <button
              onClick={downloadWithdrawalHistory}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Download size={18} />
              <span className="font-semibold">Download PDF Receipt</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Date</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Description</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-gray-600">Type</th>
                  <th className="text-right py-4 px-4 text-sm font-semibold text-gray-600">Amount</th>
                  <th className="text-center py-4 px-4 text-sm font-semibold text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length > 0 ? (
                  transactions.map((txn, index) => (
                    <tr key={txn.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-4 text-sm text-gray-600">
                        {new Date(txn.date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        <div className="text-xs text-gray-400">{new Date(txn.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-800">{txn.description}</span>
                          <span className="text-xs text-gray-500 capitalize">{txn.source}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${txn.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                          }`}>
                          {txn.type.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                          {txn.type === 'credit' ? '+' : '-'} ₹ {parseFloat(txn.amount).toLocaleString('en-IN')}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span className={`text-sm font-medium capitalize ${txn.status === 'success' || txn.status === 'completed' || txn.status === 'approved' ? 'text-green-600' :
                            txn.status === 'pending' ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                            {txn.status || 'Success'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      No transactions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Custom Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md relative animate-scaleIn">
            {/* Close button */}
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="p-8">
              <div className="mb-6">
                <p className="text-gray-400 text-sm mb-2">Notice</p>

                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <span className="text-green-500 text-xl flex-shrink-0">✓</span>
                    <p className="text-white font-medium">{modalMessage.title}</p>
                  </div>

                  <div className="flex items-start space-x-3">
                    <span className="text-yellow-500 text-xl flex-shrink-0">🔒</span>
                    <p className="text-white font-medium">{modalMessage.content}</p>
                  </div>

                  {modalMessage.password && (
                    <>
                      <div className="flex items-start space-x-3">
                        <span className="text-yellow-500 text-xl flex-shrink-0">🔑</span>
                        <p className="text-white font-medium">Password: {modalMessage.password}</p>
                      </div>

                      <div className="pt-2">
                        <p className="text-gray-300 text-sm">
                          Open the HTML file in your browser and enter your User ID to view the receipt.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-8 py-2 bg-pink-400 hover:bg-pink-500 text-gray-900 rounded-full font-semibold transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Prompt Modal */}
      {showPasswordPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md relative animate-scaleIn">
            {/* Close button */}
            <button
              onClick={() => {
                setShowPasswordPrompt(false);
                setPasswordInput('');
                setPasswordError('');
              }}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Modal Content */}
            <div className="p-8">
              <div className="mb-6">
                <div className="text-center mb-4">
                  <span className="text-6xl">🔐</span>
                </div>
                <h3 className="text-xl font-bold text-white text-center mb-2">Security Verification</h3>
                <p className="text-gray-400 text-sm text-center">Enter your Transaction Password to download the receipt</p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Transaction Password</label>
                  <input
                    type="password"
                    value={passwordInput}
                    onChange={(e) => {
                      setPasswordInput(e.target.value);
                      setPasswordError('');
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handlePasswordSubmit();
                      }
                    }}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter Transaction Password"
                    autoFocus
                  />
                  {passwordError && (
                    <p className="mt-2 text-sm text-red-400">{passwordError}</p>
                  )}
                </div>

                <button
                  onClick={handlePasswordSubmit}
                  className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-scaleIn">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50 rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-gray-800">Confirm Withdrawal Details</h3>
                <p className="text-sm text-gray-500">Verify your bank details before proceeding</p>
              </div>
              <button onClick={() => setShowConfirmModal(false)} className="text-gray-400 hover:text-gray-600 transition-colors">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Amount Summary */}
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 flex justify-between items-center">
                <span className="text-blue-800 font-medium">Withdrawal Amount</span>
                <span className="text-2xl font-bold text-blue-900">₹ {parseFloat(requestAmount).toLocaleString('en-IN')}</span>
              </div>

              {/* Bank Details Section */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                    <span>🏦</span> Bank Account Details
                  </h4>
                  <button
                    type="button"
                    onClick={() => setIsEditingBank(!isEditingBank)}
                    className={`text-sm font-medium px-3 py-1 rounded-lg transition-colors ${isEditingBank ? 'bg-gray-200 text-gray-700' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}
                  >
                    {isEditingBank ? 'Cancel Edit' : 'Edit Details'}
                  </button>
                </div>

                <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 transition-all duration-300 ${isEditingBank ? 'bg-yellow-50 p-4 rounded-xl border border-yellow-200' : 'bg-gray-50 p-4 rounded-xl border border-gray-100'}`}>
                  {['bank_name', 'account_number', 'ifsc_code', 'branch_name', 'account_holder'].map((field) => (
                    <div key={field} className={field === 'account_holder' ? 'md:col-span-2' : ''}>
                      <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                        {field.replace('_', ' ')}
                      </label>
                      {isEditingBank ? (
                        <input
                          type="text"
                          value={editableBankDetails[field]}
                          onChange={(e) => {
                            const val = field === 'ifsc_code' ? e.target.value.toUpperCase() : e.target.value;
                            setEditableBankDetails({ ...editableBankDetails, [field]: val });
                          }}
                          onBlur={field === 'ifsc_code' ? handleIFSCBlur : undefined}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-400 focus:outline-none bg-white font-medium text-gray-800"
                          placeholder={`Enter ${field.replace('_', ' ')}`}
                        />
                      ) : (
                        <div className="text-gray-900 font-semibold truncate">
                          {editableBankDetails[field] || <span className="text-gray-400 italic">Not set</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {isEditingBank && (
                  <div className="text-xs text-yellow-700 flex items-center gap-1">
                    <span>⚠️</span> Updating these details will also update your profile's saved bank information.
                  </div>
                )}
              </div>

              {/* Transaction Password */}
              <div className="pt-4 border-t border-gray-100">
                <label className="block text-sm font-bold text-gray-700 mb-2">Transaction Password <span className="text-red-500">*</span></label>
                <input
                  type="password"
                  value={transactionPassword}
                  onChange={(e) => setTransactionPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:outline-none shadow-sm"
                  placeholder="Enter your secure transaction password"
                />
              </div>
            </div>

            <div className="p-6 bg-gray-50 border-t border-gray-100 rounded-b-2xl flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-5 py-2.5 text-gray-600 font-medium hover:bg-gray-200 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitWithdrawal}
                disabled={confirmLoading || !transactionPassword}
                className={`px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg transition-all flex items-center gap-2 ${confirmLoading || !transactionPassword ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {confirmLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <span>Confirm & Submit</span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scaleIn {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>
    </Layout>
  );
};

export default Bank;
